import { Component, ChangeDetectionStrategy} from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { ToastrService } from 'ngx-toastr';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
  selector: 'app-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss']
})
export class CustomSelectComponent implements ICellRendererAngularComp {
  params: any;
  masterData: string[] = [];
  selectedValues: string;
  requiredField: boolean = false;
  filteredValues: Observable<string[]>;
  ddControl = new FormControl();
  options: string[] = [];
  disablePointerEvent: boolean = false;
  lastFilter: string = '';
  isEdited: boolean = true;
  constructor(
    private toastrService: ToastrService
  ) { }

  agInit(params): void {
    this.masterData = params.masterData;
    this.selectedValues = params.selectedValues;
    this.ddControl.setValue(this.selectedValues);
    this.params = params;
    this.initFieldState(params);
    this.initLTCNutritionChange(params);

    this.filteredValues = this.ddControl.valueChanges.pipe(
      startWith<string | string[]>(''),
      map((value) => this.updateSelectList(value)),
      map((value) => (typeof value === 'string' ? value : this.lastFilter)),
      map((filter) => this.filter(filter))
    );
  }

  private initFieldState(params: any) {
    if ((params.customFieldName === 'piStatusAtDischarge' && (params.selectedTemplate === 300 || params.selectedTemplate === 700)) || params.customFieldName === 'highestStageOfPI') {
      const piValue = this.params.data.pressureInjury;
      if (!piValue || piValue === '' || piValue === 'No' || piValue.trim().includes('(Data Error)') || piValue.trim().includes('(Missing Required Data)')) {
        this.ddControl.disable();
      } else {
        this.ddControl.enable();
      }
    } else if (params.customFieldName === 'onlyMCC' || params.customFieldName === 'onlyCC' || params.customFieldName === 'onlyCCAndNoMCC') {
      this.icdCodeChange(this.params.data.malnutrICDDiagnosisCoded, params.customFieldName);
    }
  }

  private initLTCNutritionChange(params: any) {
    if (!(params && params.sourcePage && params.sourcePage === 'LTCNutritionChange')) {
      return;
    }
    this.disablePointerEvent = params.data.ltcNutritionStatusChangeId !== 0;

    if (params.customFieldName === 'highestPressureInjury') {
      const piLTC = this.params.data.pressureInjuryIdentifiedLTC;
      if (params.data.ltcNutritionStatusChangeId === 0 && (!piLTC || piLTC === '' || piLTC === 'No')) {
        this.ddControl.disable();
      } else {
        this.ddControl.enable();
      }
    }

    if (params.customFieldName === 'reasonforDischarge') {
      const dischargeDate = this.params.data.residentExpiredOrDischargedDate;
      if (params.data.ltcNutritionStatusChangeId === 0 && (!dischargeDate || dischargeDate === '' || dischargeDate === 'No')) {
        this.ddControl.disable();
      } else {
        this.ddControl.enable();
      }
    }
  }


  refresh(): boolean {
    if (this.isEdited) {
      return false;
    }
    return true;
  }


  updateSelectList(value: any): any {
    let valueToGrid = value;
    this.isEdited = false;
    if (this.params && valueToGrid && valueToGrid.indexOf('(Data Error)') === -1) {
      this.params.data[this.params.column.colId] = valueToGrid;
      this.params.masterData = this.masterData;
      this.params.value = valueToGrid;
      this.refreshColumnByValue(valueToGrid);
    }
    if (this.params && this.ddControl.touched && (valueToGrid === '' || (typeof valueToGrid === "undefined"))) {
      this.params.data[this.params.column.colId] = '';
      this.params.masterData = this.masterData;
      this.params.value = '';
      this.refreshColumnByValue('');
    }
    this.isEdited = true;
    return value;
  }

  private refreshColumnByValue(valueToGrid: string) {
    const colId = this.params.column.colId;
    if (colId === 'pressureInjury') {
      if (valueToGrid === 'No' || valueToGrid === '') {
        this.params.data.highestStageOfPI = '';
        this.params.data.piStatusAtDischarge = '';
      }
      this.params.context.componentParent.customRefresh(['pressureInjury', 'highestStageOfPI', 'piStatusAtDischarge']);
    } else if (colId === 'malnutrICDDiagnosisCoded') {
      this.icdCodeUpdated(valueToGrid);
    } else if (colId === 'pressureInjuryIdentifiedLTC') {
      if (valueToGrid === 'No' || valueToGrid === '') {
        this.params.data.highestPressureInjury = '';
      }
      this.params.context.componentParent.customRefresh(['pressureInjuryIdentifiedLTC', 'highestPressureInjury']);
    } else {
      this.params.context.componentParent.customRefresh(colId);
    }
  }

  filter(filter: string): string[] {
    this.lastFilter = filter;
    if (filter) {
      if (this.lastFilter.indexOf(';') > 1) {
        filter = filter.split(/[; ]+/).pop().trim();
      }
      return this.masterData.filter((option) => {
        return option.toLowerCase().indexOf(filter.toLowerCase()) >= 0;
      });
    }
    else {
      return this.masterData.slice();
    }
  }

  displayFn = (value: string) => {
    let displayValue = value;
    if (value) { this.ddControl.markAsTouched(); }
    return displayValue;
  }

  optionClicked(event: Event, ddValue: string, trigger: MatAutocompleteTrigger) {
    this.selectedValues = ddValue;
    this.ddControl.setValue(this.selectedValues);
    this.ddControl.markAsTouched();
    if (trigger.panelOpen) {
      trigger.closePanel();
    }
  }

  icdCodeUpdated(valueToGrid) {
    switch (valueToGrid) {
      case 'E40':
      case 'E41':
      case 'E42':
      case 'E43':
        //this.params.data.onlyMCC = valueToGrid;
        this.params.data.onlyCCAndNoMCC = '';
        //this.params.data.onlyCC = ''
        this.params.context.componentParent.customRefresh(['onlyMCC', 'onlyCCAndNoMCC', 'onlyCC', 'drgwMalnutrition', 'drgwoMalnutrition', 'malnutrICDDiagnosisCoded']);
        break;
      case 'E44':
      case 'E44.1':
      case 'E46':
        //this.params.data.onlyCCAndNoMCC = valueToGrid;
        this.params.data.onlyMCC = '';
        this.params.data.onlyCC = ''
        this.params.context.componentParent.customRefresh(['onlyMCC', 'onlyCCAndNoMCC', 'onlyCC', 'drgwMalnutrition', 'drgwoMalnutrition', 'malnutrICDDiagnosisCoded']);
        break;
      case 'Not Coded':
        this.params.data.onlyCCAndNoMCC = '';
        this.params.data.onlyMCC = '';
        this.params.data.onlyCC = ''
        this.params.data.drgwMalnutrition = '';
        this.params.data.drgwoMalnutrition = '';
        this.params.context.componentParent.customRefresh(['onlyMCC', 'onlyCCAndNoMCC', 'onlyCC', 'drgwMalnutrition', 'drgwoMalnutrition', 'malnutrICDDiagnosisCoded']);
        break;
      default:
        this.params.data.onlyCCAndNoMCC = '';
        this.params.data.onlyMCC = '';
        this.params.data.onlyCC = ''
        this.params.data.drgwMalnutrition = '';
        this.params.data.drgwoMalnutrition = '';
        this.params.context.componentParent.customRefresh(['onlyMCC', 'onlyCCAndNoMCC', 'onlyCC', 'drgwMalnutrition', 'drgwoMalnutrition', 'malnutrICDDiagnosisCoded']);
        break;
    }
  }

  onkeyPress(e, trigger: MatAutocompleteTrigger){
    console.log(e);
    if(e.keyCode === 13){
      let valueFiltered = e.currentTarget.value;
      if(valueFiltered && valueFiltered !== null && valueFiltered.trim() !== ""){
        let value = this.masterData.filter(x => x.toLowerCase().indexOf(valueFiltered.toLowerCase()) >= 0);
        this.selectedValues = value[0];
        //this.selectedValues = ddValue;
        this.ddControl.setValue(this.selectedValues);
        this.ddControl.markAsTouched();
        if (trigger.panelOpen) {
          trigger.closePanel();
        }
        this.ddControl.setValue(this.selectedValues);
      }else{
        e.preventDefault();
      }
    }else{
      e.preventDefault();
    }
  }

  icdCodeChange(valueToGrid, customFieldName) {
    switch (valueToGrid) {
      case 'E40':
      case 'E41':
      case 'E42':
      case 'E43':
        if (customFieldName === 'onlyCCAndNoMCC' || customFieldName === 'onlyCC') {
          this.ddControl.disable();
        } else {
          this.ddControl.enable();
        }
        break;
      case 'E44':
      case 'E44.1':
      case 'E46':
        if (customFieldName === 'onlyMCC' || customFieldName === 'onlyCC') {
          this.ddControl.disable();
        } else {
          this.ddControl.enable();
        }
        break;
      case 'Not Coded':
        this.ddControl.disable();
        break;
      default:
        this.ddControl.disable();
        break;
    }
  }

  clearText() {
    this.ddControl.setValue('');
    this.selectedValues = '';
  }

  openPanel(event, trigger: MatAutocompleteTrigger){
    event.stopPropagation();
    trigger.openPanel();
  }

}